import React from 'react';
import './App.css';
import {Navigate, Route, Routes, useLocation} from "react-router-dom";
import { Spin } from 'antd';
import { DashboardConfigProvider, useDashboardConfig } from "./hooks/useDashboardConfig";
import { CharterConfigProvider, useCharterConfig } from "./hooks/useCharterConfig";
import HomePage from "./pages/HomePage";
import SigninPage from "./pages/SigninPage";
import DashboardPage from "./pages/DashboardPage";
import {UnprotectedLayout} from "./components/UnprotectedLayout";
import SurveyOfferPage from "./pages/SurveyOfferPage";
import {ModernDashboardLayout} from "./components/ModernDashboardLayout";
import {AuthProvider} from "./hooks/useAuth";
import {ImpersonationProvider} from "./hooks/useImpersonation";
import {ImpersonationBanner} from "./components/ImpersonationBanner";
import {FreeTrialBanner} from "./components/FreeTrialBanner";
import ProfilePage from "./pages/ProfilePage";
import SignupPage from "./pages/SignupPage";
import CharterPage from "./pages/CharterPage";
import ChoosePlanPage from "./pages/ChoosePlanPage";
import { PendingPaidPlanGate } from "./components/PendingPaidPlanGate";
import CharterClosedPage from "./pages/CharterClosedPage";
import ForgotPage from "./pages/ForgotPage";
import { ConfigProvider } from 'antd';
import OrganisationPage from "./pages/OrganisationPage";
import MaintenanceOverlay from "./components/MaintenanceOverlay";
import { useMaintenanceStatus } from "./hooks/useMaintenanceStatus";
import PasswordResetPage from "./pages/PasswordResetPage";
import PasswordSetPage from "./pages/PasswordSetPage";
import {IntlProvider} from "react-intl";
import localeEn from "./locale/en";
import BrandsPage from "./pages/BrandsPage";
import CustomersPage from "./pages/CustomersPage";
import SegmentationFieldsPage from "./pages/SegmentationFieldsPage";
import CustomersLayout from "./components/customers/CustomersLayout";
import CustomerImportPage from "./pages/CustomerImportPage";
import CustomerSegmentsPage from "./pages/CustomerSegmentsPage";
import CustomerIntegrationsPage from "./pages/CustomerIntegrationsPage";
import BrandDetailsPage from "./pages/BrandDetailsPage";
import { MarketerShell } from "./components/MarketerView/shell/MarketerShell";
import { MarketerBrandRedirect } from "./components/MarketerView/shell/MarketerBrandRedirect";
import { BriefingPage as MvBriefingPage } from "./components/MarketerView/pages/BriefingPage";
import { DetailPage as MvDetailPage } from "./components/MarketerView/pages/DetailPage";
import { ResponsesPage as MvResponsesPage } from "./components/MarketerView/pages/ResponsesPage";
import { BenchmarksPage as MvBenchmarksPage } from "./components/MarketerView/pages/BenchmarksPage";
import { ModelBeliefsPage as MvModelBeliefsPage } from "./components/MarketerView/pages/ModelBeliefsPage";
import { ExpertPage as MvExpertPage } from "./components/MarketerView/pages/ExpertPage";
import { ActionPlanPage as MvActionPlanPage } from "./components/MarketerView/pages/ActionPlanPage";
import BrandTrendsHistoryPage from "./pages/BrandTrendsHistoryPage";
import StatementsPage from "./pages/StatementsPage";
import QuestionTemplatesPage from "./pages/QuestionTemplatesPage";
import CreateSurveyPage from "./pages/CreateSurveyPage";
import SurveysPage from "./pages/SurveysPage";
import SurveyDetailsPage from "./pages/SurveyDetailsPage";
import EditSurveyPage from "./pages/EditSurveyPage";
import SurveyResultsPage from "./pages/SurveyResultsPage";
import DataSelectionPage from "./pages/DataSelectionPage";
import VisualizationPage from "./pages/VisualizationPage";
import BenchmarkingPage from "./pages/BenchmarkingPage";
import AIExpertPage from "./pages/AIExpertPage";
import SupportPage from "./pages/SupportPage";
import AdvicePage from "./pages/AdvicePage";
import ObjectivesPage from "./pages/ObjectivesPage";
import ToolsPage from "./pages/ToolsPage";
import {AdminLayout} from "./admin/components/AdminLayout";
import {AgencyShell} from "./agency/shell/AgencyShell";
import {AgencyPortfolioPage} from "./agency/pages/AgencyPortfolioPage";
import {AgencyComparePage} from "./agency/pages/AgencyComparePage";
import {AgencyClientScorecardPage} from "./agency/pages/AgencyClientScorecardPage";
import {AgencyActivityPage} from "./agency/pages/AgencyActivityPage";
import {AgencyBillingPage} from "./agency/pages/AgencyBillingPage";
import {AgencySettingsPage} from "./agency/pages/AgencySettingsPage";
import {AgencyTeamPage} from "./agency/pages/AgencyTeamPage";
import {AgencyContextProvider} from "./hooks/useAgencyContext";
import {AgencyBanner} from "./components/AgencyBanner";
import {AdminAgenciesPage} from "./admin/pages/AdminAgenciesPage";
import {AdminDashboard} from "./admin/pages/AdminDashboard";
import {AdminProfile} from "./admin/pages/AdminProfile";
import {OrganizationsPage} from "./admin/pages/OrganizationsPage";
import {AdminSignupFunnelPage} from "./admin/pages/AdminSignupFunnelPage";
import {AdminLiveAnalyticsPage} from "./admin/pages/AdminLiveAnalyticsPage";
import {AdminUsersPage} from "./admin/pages/AdminUsersPage";
import {AIExpertsDocumentsPage} from "./admin/pages/AIExpertsDocumentsPage";
import {AIExpertsChatPage} from "./admin/pages/AIExpertsChatPage";
import {ProtectedAdminRoute} from "./admin/components/ProtectedAdminRoute";
import {AdminSurveysPage} from "./admin/pages/AdminSurveysPage";
import {AdminSurveyDetailsPage} from "./admin/pages/AdminSurveyDetailsPage";
import {AdminSurveyResultsPage} from "./admin/pages/AdminSurveyResultsPage";
import {AdminBrandsPage} from "./admin/pages/AdminBrandsPage";
import {AdminBrandEquityPage} from "./admin/pages/AdminBrandEquityPage";
import {AdminStatementsPage} from "./admin/pages/AdminStatementsPage";
import {AdminEventsPage} from "./admin/pages/AdminEventsPage";
import AdminWaitlistPage from "./admin/pages/AdminWaitlistPage";
import {AdminOrganizationDetailsPage} from "./admin/pages/AdminOrganizationDetailsPage";
import {SystemSettingsPage} from "./admin/pages/SystemSettingsPage";
import {AdminStatementTemplatesPage} from "./admin/pages/AdminStatementTemplatesPage";
import AdminSupportPage from "./admin/pages/AdminSupportPage";
import {AdminAnalyticsPage} from "./admin/pages/AdminAnalyticsPage";
import {AdminBrandAnalyticsPage} from "./admin/pages/AdminBrandAnalyticsPage";
import MockDataWarningPage from "./admin/pages/MockDataWarningPage";
import MockDataManagementPage from "./admin/pages/MockDataManagementPage";
import AdminMessagesPage from "./admin/pages/AdminMessagesPage";
import {ResponseMonitoringPage} from "./admin/pages/ResponseMonitoringPage";
import {ReviewerDashboardPage} from "./admin/pages/response-integrity/ReviewerDashboardPage";
import {ResponseDetailPage} from "./admin/pages/response-integrity/ResponseDetailPage";
import {ChargebackListPage} from "./admin/pages/response-integrity/ChargebackListPage";
import {ChargebackDetailPage} from "./admin/pages/response-integrity/ChargebackDetailPage";
import {DetectorTuningPage} from "./admin/pages/response-integrity/DetectorTuningPage";
import {IntegrityProfilesPage} from "./admin/pages/response-integrity/IntegrityProfilesPage";
import {IntegritySettingsPage} from "./admin/pages/response-integrity/IntegritySettingsPage";
import {AdminBenchmarkingPage} from "./admin/pages/AdminBenchmarkingPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import { PublicHypothesisPage } from "./pages/PublicHypothesis/PublicHypothesisPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import {AdminCrmPage} from "./admin/pages/AdminCrmPage";
import AdminMailPage from "./admin/pages/AdminMailPage";
import {AdminLegalDocumentsPage} from "./admin/pages/AdminLegalDocumentsPage";
import {AdminSubscriptionPlansPage} from "./admin/pages/AdminSubscriptionPlansPage";
import {AdminFeaturesPage} from "./admin/pages/AdminFeaturesPage";
import {AdminFoundingCohortPage} from "./admin/pages/AdminFoundingCohortPage";
import {AdminAddonsPage} from "./admin/pages/AdminAddonsPage";
import {AdminRewardsPage} from "./admin/pages/AdminRewardsPage";
import {AdminToolsPage} from "./admin/pages/AdminToolsPage";
import {AIHubLayout} from "./admin/ai-hub/AIHubLayout";
import {AIHubHomePage} from "./admin/ai-hub/AIHubHomePage";
import {SubsystemPageShell} from "./admin/ai-hub/SubsystemPageShell";
import {SnapshotsPage} from "./admin/ai-hub/SnapshotsPage";
import {BrandKnowledgeInspectorPage} from "./admin/ai-hub/BrandKnowledgeInspectorPage";
import {BrandKnowledgeInspectorDetailPage} from "./admin/ai-hub/BrandKnowledgeInspectorDetailPage";
import {KnowledgeDistillConfigurePage} from "./admin/ai-hub/KnowledgeDistillConfigurePage";
import {FieldSuggestionsConfigurePage} from "./admin/ai-hub/FieldSuggestionsConfigurePage";
import {CorporateSitePage} from "./admin/pages/CorporateSitePage";
import {AdminCloudWatchLogsPage} from "./admin/pages/AdminCloudWatchLogsPage";
import { CookieConsentProvider } from './components/CookieConsent';

/* ------------------------------------------------------------------ *
 * SYR-79 — dashboard swap. The classic dashboard and the Marketer View
 * shell each mount under a route root that flips with the global
 * MARKETER_VIEW_DEFAULT_ENABLED flag. Child route lists are relative so the
 * same components work at either mount point.
 * ------------------------------------------------------------------ */

// Classic dashboard children (ModernDashboardLayout). Relative paths → mount at
// /dashboard (flag OFF) or /old-dashboard (flag ON).
const CLASSIC_CHILDREN = (
  <>
    <Route path="" element={<DashboardPage />} />
    <Route path="profile" element={<ProfilePage />} />
    <Route path="test" element={<div>Test Route Works!</div>} />
    <Route path="organisation" element={<OrganisationPage />} />
    <Route path="brands" element={<BrandsPage />} />
    <Route path="customers/import" element={<CustomerImportPage />} />
    <Route path="customers/import/:jobId" element={<CustomerImportPage />} />
    <Route path="customers" element={<CustomersLayout />}>
      <Route index element={<CustomersPage />} />
      <Route path="fields" element={<SegmentationFieldsPage />} />
      <Route path="segments" element={<CustomerSegmentsPage />} />
      <Route path="integrations" element={<CustomerIntegrationsPage />} />
    </Route>
    <Route path="brands/:brandHash/details" element={<BrandDetailsPage />} />
    <Route path="surveys" element={<SurveysPage />} />
    <Route path="surveys/create" element={<CreateSurveyPage />} />
    <Route path="surveys/:surveyHash" element={<SurveyDetailsPage />} />
    <Route path="surveys/:surveyHash/edit" element={<EditSurveyPage />} />
    <Route path="surveys/:surveyHash/results" element={<SurveyResultsPage />} />
    <Route path="analytics/data-selection" element={<DataSelectionPage />} />
    <Route path="analytics/visualization" element={<VisualizationPage />} />
    <Route path="analytics/statements" element={<StatementsPage />} />
    <Route path="analytics/trends" element={<BrandTrendsHistoryPage />} />
    <Route path="benchmarking" element={<BenchmarkingPage />} />
    <Route path="ai-experts" element={<AIExpertPage />} />
    <Route path="advice" element={<AdvicePage />} />
    <Route path="objectives" element={<ObjectivesPage />} />
    <Route path="tools" element={<ToolsPage />} />
    <Route path="support" element={<SupportPage />} />
    <Route path="support/:ticketHash" element={<SupportPage />} />
    <Route path="surveys/question-templates" element={<QuestionTemplatesPage />} />
  </>
);

// Marketer View shell children. Relative paths → mount at /marketer-view/:brandHash
// (flag OFF) or /dashboard/:brandHash (flag ON).
const MARKETER_CHILDREN = (
  <>
    <Route index element={<MvBriefingPage />} />
    <Route path="detail" element={<MvDetailPage />} />
    <Route path="responses" element={<MvResponsesPage />} />
    <Route path="benchmarks" element={<MvBenchmarksPage />} />
    <Route path="model-beliefs" element={<MvModelBeliefsPage />} />
    <Route path="expert" element={<MvExpertPage />} />
    <Route path="action-plan" element={<MvActionPlanPage />} />
    {/* Footer Admin + Help — same content, rendered inside the shell. */}
    <Route path="admin/surveys" element={<SurveysPage />} />
    <Route path="admin/surveys/create" element={<CreateSurveyPage />} />
    <Route path="admin/surveys/question-templates" element={<QuestionTemplatesPage />} />
    <Route path="admin/surveys/:surveyHash" element={<SurveyDetailsPage />} />
    <Route path="admin/surveys/:surveyHash/edit" element={<EditSurveyPage />} />
    <Route path="admin/surveys/:surveyHash/results" element={<SurveyResultsPage />} />
    <Route path="admin/customers/import" element={<CustomerImportPage />} />
    <Route path="admin/customers/import/:jobId" element={<CustomerImportPage />} />
    <Route path="admin/customers" element={<CustomersLayout />}>
      <Route index element={<CustomersPage />} />
      <Route path="fields" element={<SegmentationFieldsPage />} />
      <Route path="segments" element={<CustomerSegmentsPage />} />
      <Route path="integrations" element={<CustomerIntegrationsPage />} />
    </Route>
    <Route path="admin/organisation" element={<OrganisationPage />} />
    <Route path="admin/profile" element={<ProfilePage />} />
    <Route path="help" element={<SupportPage />} />
    <Route path="help/:ticketHash" element={<SupportPage />} />
  </>
);

// Classic top-level segments that keep working via a redirect shim when the
// Marketer View owns /dashboard (protects stale bookmarks + un-swept absolute
// links). Static segments outrank /dashboard/:brandHash so these win.
const CLASSIC_SEGMENTS = [
  'profile', 'organisation', 'brands', 'customers', 'surveys', 'analytics',
  'benchmarking', 'ai-experts', 'advice', 'objectives', 'tools', 'support',
];

/** Rewrite the leading route root of the current URL, preserving search + hash. */
const RootRedirect: React.FC<{ from: string; to: string }> = ({ from, to }) => {
  const loc = useLocation();
  const target = loc.pathname.replace(from, to) + loc.search + loc.hash;
  return <Navigate to={target} replace />;
};

function AppRoutes() {
  const { marketerViewDefault, ready } = useDashboardConfig();
  const { charterCohortEnabled, ready: charterReady } = useCharterConfig();

  // Hold routing until the global flags are known so we never mount the wrong
  // shell at /dashboard, or flash the wrong page at /charter, and remount when
  // the config arrives.
  if (!ready || !charterReady) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<UnprotectedLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/sign-in" element={<SigninPage />} />
        <Route path="/forgot" element={<ForgotPage />} />
        {/* SYR-109: Charter sits ALONGSIDE normal signup. The flag opens the
            application at /charter; it no longer takes /sign-up over. Whether
            registration is open is PUBLIC_SIGNUP_ENABLED, which SignupPage
            renders (closed-beta panel, waitlist invite tokens excepted) and
            the API now enforces server-side. */}
        <Route path="/sign-up" element={<SignupPage />} />
        {charterCohortEnabled ? (
          <Route path="/charter" element={<CharterPage />} />
        ) : (
          /* The marketing site cannot know the intake has closed, so its
             "Apply" button keeps pointing here. Say so rather than bouncing
             silently to a login form. */
          <Route path="/charter" element={<CharterClosedPage />} />
        )}
        <Route path="/password-reset/:resetToken" element={<PasswordResetPage />} />
        <Route path="/password-set/:resetToken" element={<PasswordSetPage />} />
      </Route>

      {/* Legal pages - accessible to both authenticated and unauthenticated users */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />

      {/* SYR-105 phase 2 — the brand's offer screen, reached from the pack email.
          Top level and outside every layout on purpose: it is one focused task
          for a contact who barely knows the platform, and it guards itself so an
          unauthenticated arrival keeps the token through sign-in. */}
      <Route path="/survey-offer" element={<SurveyOfferPage />} />

      {/* Unsubscribe routes - accessible to both authenticated and unauthenticated users */}
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
      <Route path="/unsubscribe/:token/:messageTypeCode" element={<UnsubscribePage />} />

      {/* SYR-107 — the public Hypothesis lead generator, iframed by
          brandscience.ai/hypothesis. Top level and outside every layout on
          purpose: UnprotectedLayout redirects an authenticated user to
          /dashboard, which would break the emailed read-out link for anyone
          who happens to be signed in. */}
      <Route path="/hypothesis" element={<PublicHypothesisPage mode="tool" />} />
      <Route path="/hypothesis/result" element={<PublicHypothesisPage mode="result" />} />
      <Route
        path="/hypothesis/unsubscribe"
        element={<PublicHypothesisPage mode="unsubscribe" />}
      />

      {/* SYR-109. An org still carrying pendingPaidPlan sees the choose-plan
          screen instead of a dashboard. The page sits OUTSIDE the gate, and
          its static segment outranks /dashboard/:brandHash, so the Marketer
          shell never swallows it. */}
      <Route path="/dashboard/choose-plan" element={<ChoosePlanPage />} />
      <Route element={<PendingPaidPlanGate />}>
      {marketerViewDefault ? (
        <>
          {/* SYR-79 ON: Marketer View owns /dashboard; classic parked at /old-dashboard. */}
          <Route path="/dashboard" element={<MarketerBrandRedirect />} />
          <Route path="/dashboard/:brandHash" element={<MarketerShell />}>
            {MARKETER_CHILDREN}
          </Route>
          <Route path="/old-dashboard" element={<ModernDashboardLayout />}>
            {CLASSIC_CHILDREN}
          </Route>
          {/* Shims: stale classic links → /old-dashboard; legacy marketer links → /dashboard. */}
          {CLASSIC_SEGMENTS.map((seg) => (
            <Route
              key={seg}
              path={`/dashboard/${seg}/*`}
              element={<RootRedirect from="/dashboard" to="/old-dashboard" />}
            />
          ))}
          <Route path="/marketer-view" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/marketer-view/*"
            element={<RootRedirect from="/marketer-view" to="/dashboard" />}
          />
        </>
      ) : (
        <>
          {/* SYR-79 OFF (default): classic owns /dashboard; Marketer View at /marketer-view. */}
          <Route path="/dashboard" element={<ModernDashboardLayout />}>
            {CLASSIC_CHILDREN}
          </Route>
          <Route path="/marketer-view" element={<MarketerBrandRedirect />} />
          <Route path="/marketer-view/:brandHash" element={<MarketerShell />}>
            {MARKETER_CHILDREN}
          </Route>
        </>
      )}
      </Route>

      {/* Agency routes */}
      <Route path="/agency" element={<AgencyShell />}>
        <Route path="" element={<AgencyPortfolioPage />} />
        <Route path="compare" element={<AgencyComparePage />} />
        <Route path="clients/:clientOrgId" element={<AgencyClientScorecardPage />} />
        <Route path="activity" element={<AgencyActivityPage />} />
        <Route path="billing" element={<AgencyBillingPage />} />
        <Route path="settings" element={<AgencySettingsPage />} />
        <Route path="team" element={<AgencyTeamPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="support/:ticketHash" element={<SupportPage />} />
      </Route>

      <Route path="/admin" element={<AdminLayout />}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="profile" element={<AdminProfile />} />
        <Route path="organizations" element={<OrganizationsPage />} />
        <Route path="agencies" element={<AdminAgenciesPage />} />
        <Route path="signup-funnel" element={<AdminSignupFunnelPage />} />
        <Route path="organizations/:id" element={<AdminOrganizationDetailsPage />} />
        <Route path="crm" element={<AdminCrmPage />} />
        <Route path="mail" element={<AdminMailPage />} />
        {/* SYR-105 — the queue moved into the CRM tab set; keep the old URL alive. */}
        <Route path="concierge" element={<Navigate to="/admin/crm?tab=concierge" replace />} />
        <Route path="bsai-customers" element={<Navigate to="/admin/crm" replace />} />
        <Route path="brands" element={<AdminBrandsPage />} />
        <Route path="brands/:brandId/brand-equity" element={<AdminBrandEquityPage />} />
        <Route path="brands/:brandId/statements" element={<AdminStatementsPage />} />
        <Route path="surveys" element={<AdminSurveysPage />} />
        <Route path="surveys/:surveyHash" element={<AdminSurveyDetailsPage />} />
        <Route path="surveys/:surveyHash/results" element={<AdminSurveyResultsPage />} />
        <Route path="users" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <AdminUsersPage />
            </ProtectedAdminRoute>
        } />
        <Route path="ai" element={<AIHubLayout />}>
            <Route index element={<AIHubHomePage />} />
            <Route path="snapshots" element={<SnapshotsPage />} />
            <Route path="knowledge_base/documents" element={<AIExpertsDocumentsPage />} />
            <Route path="knowledge_base/brands" element={<BrandKnowledgeInspectorPage />} />
            <Route path="knowledge_base/brands/:brandId" element={<BrandKnowledgeInspectorDetailPage />} />
            <Route path="knowledge-distill" element={<KnowledgeDistillConfigurePage />} />
            <Route path="field-suggestions" element={<FieldSuggestionsConfigurePage />} />
            <Route path=":subsystemId" element={<SubsystemPageShell />} />
        </Route>
        <Route path="ai-experts/documents" element={<Navigate to="/admin/ai/knowledge_base/documents" replace />} />
        <Route path="ai-experts/chat" element={<AIExpertsChatPage />} />
        <Route path="trends" element={<AdminAnalyticsPage />} />
        <Route path="trends/brands/:brandHash" element={<AdminBrandAnalyticsPage />} />
        <Route path="benchmarking" element={<AdminBenchmarkingPage />} />
        <Route path="benchmarking/:brandHash" element={<AdminBenchmarkingPage />} />
        <Route path="messages" element={<AdminMessagesPage />} />
        <Route path="response-monitoring" element={<ResponseMonitoringPage />} />
        <Route path="response-integrity" element={<ReviewerDashboardPage />} />
        <Route path="response-integrity/responses/:id" element={<ResponseDetailPage />} />
        <Route path="response-integrity/chargebacks" element={<ChargebackListPage />} />
        <Route path="response-integrity/chargebacks/:id" element={<ChargebackDetailPage />} />
        <Route path="response-integrity/detectors" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <DetectorTuningPage />
            </ProtectedAdminRoute>
        } />
        <Route path="response-integrity/profiles" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <IntegrityProfilesPage />
            </ProtectedAdminRoute>
        } />
        <Route path="response-integrity/settings" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <IntegritySettingsPage />
            </ProtectedAdminRoute>
        } />
        <Route path="events" element={<AdminEventsPage />} />
        <Route path="live-analytics" element={<AdminLiveAnalyticsPage />} />
        <Route path="waitlist" element={<AdminWaitlistPage />} />
        <Route path="support" element={<AdminSupportPage />} />
        <Route path="support/:ticketHash" element={<AdminSupportPage />} />
        <Route path="statement-templates" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <AdminStatementTemplatesPage />
            </ProtectedAdminRoute>
        } />
        <Route path="system-settings" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <SystemSettingsPage />
            </ProtectedAdminRoute>
        } />
        <Route path="cloudwatch-logs" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <AdminCloudWatchLogsPage />
            </ProtectedAdminRoute>
        } />
        <Route path="legal-documents" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <AdminLegalDocumentsPage />
            </ProtectedAdminRoute>
        } />
        <Route path="mock-data" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <MockDataWarningPage />
            </ProtectedAdminRoute>
        } />
        <Route path="mock-data/management" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <MockDataManagementPage />
            </ProtectedAdminRoute>
        } />
        <Route path="subscription-plans" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <AdminSubscriptionPlansPage />
            </ProtectedAdminRoute>
        } />
        <Route path="features" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <AdminFeaturesPage />
            </ProtectedAdminRoute>
        } />
        <Route path="founding-cohort" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <AdminFoundingCohortPage />
            </ProtectedAdminRoute>
        } />
        <Route path="addons" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <AdminAddonsPage />
            </ProtectedAdminRoute>
        } />
        <Route path="rewards" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <AdminRewardsPage />
            </ProtectedAdminRoute>
        } />
        <Route path="tools" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <AdminToolsPage />
            </ProtectedAdminRoute>
        } />
        <Route path="corporate-site" element={
            <ProtectedAdminRoute requireSuperAdmin>
                <CorporateSitePage />
            </ProtectedAdminRoute>
        } />
      </Route>
    </Routes>
  );
}

function App() {
  const { status } = useMaintenanceStatus();

  return (
      <ConfigProvider
          theme={{
              token: {
                  // Modern SaaS Theme - BrandScience.ai Rebrand (Amber/Gold)
                  colorPrimary: '#F59E0B', // Amber - primary brand color
                  colorPrimaryHover: '#D97706', // amber-600
                  colorPrimaryActive: '#B45309', // amber-700
                  colorTextBase: '#0F172A', // slate-900 for text
                  colorTextSecondary: '#64748B', // slate-500
                  fontSize: 16,
                  fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  colorLink: '#F59E0B',
                  colorSuccess: '#10b981',
                  colorWarning: '#f59e0b',
                  colorError: '#ef4444',
                  colorInfo: '#3b82f6',
                  borderRadius: 12,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  colorBgContainer: '#ffffff',
                  colorBgLayout: '#F8FAFC', // slate-50
                  colorBorder: '#e5e7eb',
                  colorBorderSecondary: '#f3f4f6',
              },
              components: {
                  Layout: {
                      headerBg: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                      siderBg: '#ffffff',
                      bodyBg: '#F8FAFC',
                  },
                  Card: {
                      borderRadius: 16,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  },
                  Button: {
                      borderRadius: 8,
                      fontWeight: 600,
                  },
                  Input: {
                      borderRadius: 8,
                  },
                  Select: {
                      borderRadius: 8,
                  },
                  Switch: {
                      colorPrimary: '#F59E0B',
                      colorPrimaryHover: '#D97706',
                  },
              },
          }}
      >
          <CookieConsentProvider>
              <IntlProvider
                  messages={localeEn.messages}
                  locale={localeEn.code}
              >
                  <AuthProvider>
                      <ImpersonationProvider>
                          <AgencyContextProvider>
                          <ImpersonationBanner />
                          <AgencyBanner />
                          <FreeTrialBanner />
                          {status && <MaintenanceOverlay status={status} />}
                          <DashboardConfigProvider>
                              <CharterConfigProvider>
                                  <AppRoutes />
                              </CharterConfigProvider>
                          </DashboardConfigProvider>
                          </AgencyContextProvider>
                      </ImpersonationProvider>
                  </AuthProvider>
              </IntlProvider>
          </CookieConsentProvider>
      </ConfigProvider>
  );
}

// @ts-ignore
export default App;
